import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Stack,
  Terminal,
  FileCode,
  Lightning,
  FloppyDisk,
  Archive,
  ArrowRight,
} from "@phosphor-icons/react";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [provider, setProvider] = useState("jamf");
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [saveTarget, setSaveTarget] = useState("new");
  const [title, setTitle] = useState("");

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    templates.forEach((t) => {
      (g[t.category] = g[t.category] || []).push(t);
    });
    return g;
  }, [templates]);

  const pick = (t) => {
    setSelected(t);
    setResult(null);
    setTitle(t.name);
    const init = {};
    t.params.forEach((p) => (init[p.key] = p.default));
    setValues(init);
  };

  const setVal = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const render = async () => {
    if (!selected) return;
    setRendering(true);
    setResult(null);
    try {
      const { data } = await api.post("/templates/render", {
        template_id: selected.id,
        provider,
        params: values,
      });
      setResult(data);
      toast.success("Generated — 0 tokens used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Render failed");
    } finally {
      setRendering(false);
    }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: title || selected.name,
          provider: result.provider,
          output_type: result.output_type,
          prompt: `Template: ${selected.name}`,
          content: result.content,
          language: result.language,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        setSaveTarget(data.id);
        toast.success("Saved as new project");
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: result.content,
          language: result.language,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
          prompt: `Template: ${selected.name}`,
          note: selected.name,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadBundle = async () => {
    if (!result) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: title || selected.name,
          provider: result.provider,
          output_type: result.output_type,
          content: result.content,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.provider}-bundle.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch {
      toast.error("Bundle download failed");
    } finally {
      setBundling(false);
    }
  };

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <Stack size={22} className="text-[#00F0FF]" /> Templates
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Pre-built, parameterized artifacts — generated instantly.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-px bg-[#27272A] min-h-[calc(100vh-97px)]">
        {/* Template list */}
        <div className="bg-[#0A0A0B] p-6 overflow-auto" data-testid="template-list">
          {Object.keys(grouped).sort().map((cat) => (
            <div key={cat} className="mb-6">
              <div className="font-mono text-[10px] text-[#71717A] mb-2 uppercase tracking-wider">{cat}</div>
              <div className="space-y-1">
                {grouped[cat].map((t) => {
                  const Icon = t.output_type === "profile" ? FileCode : Terminal;
                  const active = selected?.id === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`template-${t.id}`}
                      onClick={() => pick(t)}
                      className={`w-full flex items-start gap-3 p-3 border text-left transition-colors duration-200 ${
                        active ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-[#27272A] hover:border-[#3f3f46]"
                      }`}
                    >
                      <Icon size={16} className={active ? "text-[#00F0FF] mt-0.5" : "text-[#A1A1AA] mt-0.5"} />
                      <div className="min-w-0">
                        <div className="text-sm">{t.name}</div>
                        <div className="font-mono text-[10px] text-[#71717A] mt-0.5 line-clamp-2">{t.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Config + output */}
        <div className="bg-[#0A0A0B] p-8">
          {!selected ? (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center border border-dashed border-[#27272A] text-center px-8">
              <Stack size={40} className="text-[#27272A] mb-4" />
              <p className="text-sm text-[#71717A] font-mono">Select a template to configure and generate.</p>
            </div>
          ) : (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl tracking-tight">{selected.name}</h2>
                <p className="text-sm text-[#A1A1AA] font-mono mt-1">{selected.desc}</p>
              </div>

              {/* Provider */}
              <div>
                <label className="block font-mono text-xs text-[#71717A] mb-3">MDM PROVIDER</label>
                <div className="flex flex-wrap gap-2">
                  {providers.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`tpl-provider-${p.id}`}
                      onClick={() => setProvider(p.id)}
                      className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${
                        provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Params */}
              {selected.params.length > 0 && (
                <div className="space-y-4">
                  <label className="block font-mono text-xs text-[#71717A]">PARAMETERS</label>
                  {selected.params.map((p) => (
                    <div key={p.key} className="grid grid-cols-1 md:grid-cols-[220px_1fr] md:items-center gap-2">
                      <label className="text-sm text-[#A1A1AA]">{p.label}</label>
                      {p.type === "bool" ? (
                        <button
                          data-testid={`param-${p.key}`}
                          onClick={() => setVal(p.key, !values[p.key])}
                          className={`w-14 h-7 border flex items-center px-0.5 transition-colors duration-200 ${
                            values[p.key] ? "border-[#00F0FF] bg-[#00F0FF]/10 justify-end" : "border-[#27272A] justify-start"
                          }`}
                        >
                          <span className={`w-5 h-5 ${values[p.key] ? "bg-[#00F0FF]" : "bg-[#52525b]"}`} />
                        </button>
                      ) : p.type === "select" ? (
                        <select
                          data-testid={`param-${p.key}`}
                          value={values[p.key]}
                          onChange={(e) => setVal(p.key, e.target.value)}
                          className="bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                        >
                          {p.options.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          data-testid={`param-${p.key}`}
                          type={p.type === "password" ? "password" : p.type === "number" ? "number" : "text"}
                          value={values[p.key]}
                          onChange={(e) => setVal(p.key, p.type === "number" ? e.target.value : e.target.value)}
                          className="bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button
                data-testid="render-template-btn"
                onClick={render}
                disabled={rendering}
                className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50"
              >
                <Lightning size={16} weight="fill" /> {rendering ? "Generating…" : "Generate (0 tokens)"}
                {!rendering && <ArrowRight size={16} />}
              </button>

              {result && (
                <div className="space-y-4" data-testid="template-output">
                  <div className="flex flex-col md:flex-row gap-3">
                    <select
                      data-testid="tpl-save-target"
                      value={saveTarget}
                      onChange={(e) => setSaveTarget(e.target.value)}
                      className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                    >
                      <option value="new">＋ New project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>
                      ))}
                    </select>
                    {saveTarget === "new" && (
                      <input
                        data-testid="tpl-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Project title"
                        className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                      />
                    )}
                    <div className="flex gap-3">
                      <button
                        data-testid="tpl-save-btn"
                        onClick={save}
                        disabled={saving}
                        className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"
                      >
                        <FloppyDisk size={16} /> {saving ? "…" : "Save"}
                      </button>
                      <button
                        data-testid="tpl-bundle-btn"
                        onClick={downloadBundle}
                        disabled={bundling}
                        className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50"
                      >
                        <Archive size={16} /> {bundling ? "…" : "Bundle"}
                      </button>
                    </div>
                  </div>

                  <CodeBlock code={result.content} language={result.language} filename={result.filename} />

                  {result.deploy_notes && (
                    <div className="border border-[#27272A] bg-[#121214] p-4">
                      <div className="font-mono text-xs text-[#00F0FF] mb-2">DEPLOYMENT · {result.provider.toUpperCase()}</div>
                      <p className="text-sm text-[#A1A1AA] leading-relaxed">{result.deploy_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
